import { useMemo, useState } from 'react';

export type LocationOption = { id: number; label: string };
export type LocationGroup = { state: string; options: LocationOption[] };

type LocationPickerListProps = {
	groups: LocationGroup[];
	selectedIds: number[];
	/** Label of the pinned "no specific location" choice; it is active whenever the selection is empty. */
	noneLabel: string;
	/** Single mode replaces the selection with the clicked option; multiple mode toggles membership. */
	single?: boolean;
	onChange: (nextIds: number[]) => void;
	/** Called after a single-mode pick. */
	onDone?: () => void;
	autoFocusSearch?: boolean;
	disabled?: boolean;
};

export function LocationPickerList({
	groups,
	selectedIds,
	noneLabel,
	single = false,
	onChange,
	onDone,
	autoFocusSearch = false,
	disabled = false,
}: LocationPickerListProps) {
	const [query, setQuery] = useState('');
	const needle = query.trim().toLowerCase();
	const filteredGroups = useMemo(
		() =>
			(needle
				? groups.map((g) => ({ state: g.state, options: g.options.filter((o) => o.label.toLowerCase().includes(needle)) }))
				: groups
			).filter((g) => g.options.length > 0),
		[groups, needle]
	);

	const pick = (nextIds: number[]) => {
		onChange(nextIds);
		if (single) {
			onDone?.();
		}
	};

	return (
		<div className="space-y-2">
			<input
				type="search"
				value={query}
				onChange={(e) => setQuery(e.target.value)}
				placeholder="Search locations…"
				aria-label="Search locations"
				disabled={disabled}
				data-autofocus={autoFocusSearch ? true : undefined}
				className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-white text-gray-900 text-base focus:ring-2 focus:ring-smartfield focus:border-transparent"
			/>
			<div
				role="listbox"
				aria-multiselectable={!single}
				className="max-h-64 overflow-y-auto border border-gray-300 rounded-lg divide-y divide-gray-100 bg-white"
			>
				<OptionRow label={noneLabel} selected={selectedIds.length === 0} disabled={disabled} onSelect={() => pick([])} />
				{filteredGroups.map((g) => (
					<div key={g.state}>
						<div className="px-3 pt-2 pb-1 text-xs font-semibold uppercase tracking-wide text-gray-500">{g.state}</div>
						{g.options.map((o) => (
							<OptionRow
								key={o.id}
								label={o.label}
								selected={selectedIds.includes(o.id)}
								disabled={disabled}
								onSelect={() =>
									pick(
										single
											? [o.id]
											: selectedIds.includes(o.id)
												? selectedIds.filter((id) => id !== o.id)
												: [...selectedIds, o.id],
									)
								}
							/>
						))}
					</div>
				))}
				{needle && filteredGroups.length === 0 && (
					<p className="px-3 py-2 text-sm text-gray-500">No locations match &quot;{query.trim()}&quot;.</p>
				)}
			</div>
		</div>
	);
}

function OptionRow({ label, selected, disabled, onSelect }: { label: string; selected: boolean; disabled: boolean; onSelect: () => void }) {
	return (
		<button
			type="button"
			role="option"
			aria-selected={selected}
			disabled={disabled}
			onClick={onSelect}
			className={`w-full flex items-center justify-between gap-2 px-3 py-2.5 text-left text-base sm:text-sm ${
				selected ? 'bg-smartfield/10 font-medium text-gray-900' : 'text-gray-700'
			}`}
		>
			<span className="truncate">{label}</span>
			{selected && (
				<svg viewBox="0 0 20 20" fill="currentColor" aria-hidden="true" className="w-4 h-4 shrink-0 text-smartfield">
					<path
						fillRule="evenodd"
						d="M16.704 5.63a.75.75 0 0 1 .143 1.052l-7.393 9.393a.75.75 0 0 1-1.127.073l-3.5-3.5a.75.75 0 0 1 1.06-1.06l2.946 2.945 6.822-8.67a.75.75 0 0 1 1.05-.143Z"
						clipRule="evenodd"
					/>
				</svg>
			)}
		</button>
	);
}
